import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
	Grid,
	Typography,
	Card,
	CardContent,
	Box,
	CircularProgress,
	Fade,
	ImageList,
	ImageListItem,
	Fab,
	Alert,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
} from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { GET_INTERVENTIONS_BY_MACHINE } from "../graphql/querys/interventionsByMachine";
import { EDIT_INTERVENTION_MUTATION } from "../graphql/mutation/editIntervention";
import { uploadInterventionPhotos } from "../services/intervention-photo-upload";

const pendingPhoto = (file) => ({ file, preview: URL.createObjectURL(file) });
const photoSource = (photo) => photo?.preview || photo;
const photoValue = (photo) => photo?.file || photo;

function InterventionHistory() {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const { loading, error, data, refetch } = useQuery(
		GET_INTERVENTIONS_BY_MACHINE,
		{
			variables: { id },
		}
	);

	const [editIntervention] = useMutation(EDIT_INTERVENTION_MUTATION);

	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [currentIntervention, setCurrentIntervention] = useState(null);
	const [description, setDescription] = useState("");
	const [photos, setPhotos] = useState([]);
	const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
	const [uploadError, setUploadError] = useState("");

	useEffect(() => {
		refetch();
	}, []);

	const handleEditIntervention = (intervention) => {
		setCurrentIntervention(intervention);
		setDescription(intervention.obs || "");
		setPhotos(intervention.photosTaken || []);
		setUploadError("");
		setOpenEditDialog(true);
	};

	const handleDeletePhoto = (idx) => {
		const updatedPhotos = [...photos];
		const [removedPhoto] = updatedPhotos.splice(idx, 1);
		if (removedPhoto?.preview) URL.revokeObjectURL(removedPhoto.preview);
		setPhotos(updatedPhotos);
	};

	const handlePhotoUpload = (event) => {
		const files = event.target.files;
		setPhotos((prevPhotos) => [
			...prevPhotos,
			...Array.from(files).map(pendingPhoto),
		]);
		event.target.value = "";
	};

	const handleSaveChanges = async () => {
		setUploadError("");
		setIsUploadingPhotos(true);
		try {
			const photoUrls = await uploadInterventionPhotos(photos.map(photoValue));
			await editIntervention({
				variables: {
					input: {
						machineId: currentIntervention.machineId,
						obs: description,
						photosTaken: photoUrls,
						interventionId: currentIntervention._id,
					},
				},
			});
			setOpenEditDialog(false);
			refetch();
		} catch (error) {
			console.error("Error updating intervention:", error);
			setUploadError(error?.message || "Não foi possível guardar a intervenção.");
		} finally {
			setIsUploadingPhotos(false);
		}
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box p={4}>
				<Alert severity="error">{error.message}</Alert>
			</Box>
		);
	}

	return (
		<Box p={3} bgcolor="#f4f4f4" minHeight="100vh">
			<Fab
				onClick={() => {
					navigate(`/machines-mobile/${id}`);
				}}
				sx={{
					margin: 0,
					top: 20,
					right: 20,
					position: "fixed",
				}}
				color="primary"
				aria-label={t("ACTIONS.BACK")}>
				<FirstPageIcon />
			</Fab>

			<Typography variant="h4" gutterBottom>
				{t("INTERVENTIONS.HISTORY_TITLE")}
			</Typography>

			<Grid container spacing={3} direction="column">
				{data.interventionsByMachine.map((intervention) => (
					<Fade in timeout={500} key={intervention._id}>
						<Grid item>
							<Card
								variant="outlined"
								sx={{
									borderRadius: "15px",
									boxShadow: 3,
									transition: "transform 0.2s ease-in-out",
									"&:hover": {
										transform: "scale(1.02)",
									},
								}}>
								<CardContent>
									<Typography variant="h6" gutterBottom>
										{t("INTERVENTIONS.DONE_BY", {
											name: intervention.technicianInfo.name,
										})}
									</Typography>
									<Typography variant="body2" color="textSecondary">
										{t("INTERVENTIONS.DONE_AT", {
											date: new Date(intervention.createdAt).toLocaleDateString(),
										})}
									</Typography>
									<Typography variant="body1" gutterBottom>
										{t("INTERVENTIONS.TYPE", {
											type:
												intervention.type === "REPAIR"
													? t("MOBILE_MACHINE.REPAIR")
													: t("MOBILE_MACHINE.MAINTENANCE"),
										})}
									</Typography>
									<Typography variant="body1" gutterBottom>
										{t("INTERVENTIONS.DESCRIPTION", {
											description: intervention.obs,
										})}
									</Typography>
									<Typography variant="body1" gutterBottom>
										{t("INTERVENTIONS.PHOTOS")}
									</Typography>

									<ImageList cols={3} gap={10}>
										{intervention.photosTaken.map((photo, index) => (
											<ImageListItem key={index}>
												<img
													src={photo}
													alt={`Photo ${index}`}
													style={{
														borderRadius: "10px",
														cursor: "pointer",
														transition: "transform 0.3s ease",
													}}
												/>
											</ImageListItem>
										))}
									</ImageList>
									<Button
										variant="outlined"
										color="primary"
										sx={{ mt: 2 }}
										onClick={() => handleEditIntervention(intervention)}>
										{t("INTERVENTIONS.EDIT")}
									</Button>
								</CardContent>
							</Card>
						</Grid>
					</Fade>
				))}
			</Grid>

			<Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
				<DialogTitle>{t("INTERVENTIONS.EDIT_TITLE")}</DialogTitle>
				<DialogContent>
					<TextField
						label={t("FIELDS.description")}
						fullWidth
						multiline
						rows={4}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						margin="dense"
					/>

					<Typography variant="subtitle1" gutterBottom>
						{t("INTERVENTIONS.PHOTOS")}
					</Typography>
					<Grid container spacing={1} alignItems="center">
						{photos.map((photo, idx) => (
							<Grid item key={idx} style={{ position: "relative" }}>
								<img
									src={photoSource(photo)}
									alt={`Photo ${idx}`}
									width="80"
									height="80"
									style={{
										borderRadius: "4px",
										objectFit: "cover",
										boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
									}}
								/>
								<IconButton
									onClick={() => handleDeletePhoto(idx)}
									style={{
										position: "absolute",
										top: "-5px",
										right: "-5px",
										color: "white",
										backgroundColor: "red",
										padding: "2px",
										borderRadius: "50%",
									}}
									size="small">
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Grid>
						))}

						<Grid item>
							<input
								accept="image/*"
								style={{ display: "none" }}
								id="upload-photo"
								type="file"
								multiple
								onChange={handlePhotoUpload}
							/>
							<label htmlFor="upload-photo">
								<IconButton
									color="primary"
									aria-label={t("ACTIONS.UPLOAD_PHOTOS")}
									component="span"
									style={{
										borderRadius: "4px",
										width: "80px",
										height: "80px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										border: "1px solid #ccc",
										backgroundColor: "#f0f0f0",
									}}>
									<CameraAltIcon fontSize="large" />
								</IconButton>
							</label>
						</Grid>
					</Grid>
					{uploadError && <Alert severity="error">{uploadError}</Alert>}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenEditDialog(false)} color="secondary">
						{t("ACTIONS.CANCEL")}
					</Button>
					<Button onClick={handleSaveChanges} color="primary" disabled={isUploadingPhotos}>
						{isUploadingPhotos ? <CircularProgress size={24} /> : t("ACTIONS.SAVE")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default InterventionHistory;
